import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * Dialog — shadcn idiom on `@radix-ui/react-dialog`, themed onto the HofSync
 * brand tokens. Like Select/Tooltip, this is a Radix **portal** primitive: the
 * overlay and panel render through `DialogPrimitive.Portal` to `document.body`,
 * OUTSIDE the DS subtree. The global compiled stylesheet `dist/styles.css`
 * still styles them, but ONLY because every utility on the portal content is
 * `tw:`-prefixed — drop the prefix and the dialog renders unstyled in the app.
 * (See CLAUDE.md "Scoping".)
 *
 * The overlay scrim is `tw:bg-black/50` — deliberately NOT a semantic surface
 * token: it matches the legacy `.confirmation-modal-backdrop` scrim
 * (rgba(0,0,0,.5)) in BOTH themes, so shadcn and plain-CSS modals dim the page
 * identically. Everything else references semantic tokens only.
 *
 * Preflight is excluded from the DS Tailwind build, so the built-in close
 * trigger — a native `<button>` — keeps its UA chrome unless reset
 * per-component: `tw:appearance-none tw:border-0 tw:bg-transparent` + explicit
 * focus-ring + `tw:font-sans` (form controls don't inherit font-family, #1927).
 *
 * A11y contract: consumers MUST render `DialogTitle` (and ideally
 * `DialogDescription`) inside `DialogContent` — Radix warns without them and
 * axe fails the dialog-name check. `@radix-ui/react-dialog` is a real
 * `dependency` (the DS ships as source); the names are collision-free in the
 * public-API barrel.
 */
const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

export type DialogProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Root
>;
export type DialogContentProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> & {
  /** Accessible label for the built-in close button (default: German). */
  closeLabel?: string;
};
export type DialogOverlayProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Overlay
>;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  DialogOverlayProps
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    // Portal content: every utility MUST be tw:-prefixed (it mounts on
    // document.body, styled only by the global dist/styles.css). Scrim =
    // black/50 for parity with the legacy .confirmation-modal-backdrop.
    className={cn('tw:fixed tw:inset-0 tw:z-50 tw:bg-black/50', className)}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, closeLabel = 'Schließen', ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      // Portal content: every utility MUST be tw:-prefixed (see above).
      className={cn(
        'tw:fixed tw:left-1/2 tw:top-1/2 tw:z-50 tw:grid tw:w-full tw:max-w-lg tw:-translate-x-1/2 tw:-translate-y-1/2 tw:gap-4 tw:rounded-lg tw:border tw:border-border tw:bg-card tw:p-6 tw:text-foreground tw:shadow-lg',
        className
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close
        // Native <button>: full UA reset (appearance/border/background/FONT,
        // #1927) — preflight is excluded, nothing resets it globally.
        className="tw:absolute tw:right-4 tw:top-4 tw:inline-flex tw:appearance-none tw:items-center tw:justify-center tw:rounded-sm tw:border-0 tw:bg-transparent tw:p-1 tw:font-sans tw:text-foreground tw:opacity-70 tw:transition-opacity tw:hover:opacity-100 tw:focus-visible:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-ring tw:disabled:pointer-events-none"
      >
        <X className="tw:h-4 tw:w-4" />
        <span className="tw:sr-only">{closeLabel}</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'tw:flex tw:flex-col tw:gap-1.5 tw:text-center tw:sm:text-left',
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'tw:flex tw:flex-col-reverse tw:gap-2 tw:sm:flex-row tw:sm:justify-end',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'tw:text-lg tw:font-semibold tw:leading-none tw:tracking-tight tw:text-foreground',
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('tw:text-sm tw:text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
